import { type ReactNode, useId } from 'react'
import { Notice } from '../shell/notice.tsx'

/**
 * The container every block of the tenant's Overview is built from — docs/ux/PLATFORM-ADMIN.md
 * §11, and §18.1's rule that a page renders its panels while their data is on the way.
 *
 * It is a `<section>` named by its own heading, so each card is a region a screen reader can
 * jump to **and the heading is there while the card is still loading** — the structure is
 * constant, only the body changes. A card's head may carry one action (a link to the editor, a
 * danger button), which is why the head is a flex row.
 *
 * `wide` spans both columns of the grid.
 */
export function Card({
  title,
  description,
  action,
  wide = false,
  busy = false,
  children,
}: {
  readonly title: string
  readonly description?: string
  readonly action?: ReactNode
  readonly wide?: boolean
  /** The card's own data has not arrived: the region says so to assistive technology. */
  readonly busy?: boolean
  readonly children?: ReactNode
}) {
  const headingId = useId()

  return (
    <section
      className={wide ? 'card span-2' : 'card'}
      aria-labelledby={headingId}
      aria-busy={busy || undefined}
    >
      <div className="card-head">
        <div>
          <h2 id={headingId}>{title}</h2>
          {description === undefined ? null : <p className="muted">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Placeholder lines in the shape of a card's body (§18.1): never a spinner. */
export function CardSkeleton({ lines = 3 }: { readonly lines?: number }) {
  return (
    <div className="card-body" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <span
          // The lines are interchangeable and never reorder.
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
          key={index}
          className={index === lines - 1 ? 'skeleton skeleton-narrow' : 'skeleton'}
        />
      ))}
    </div>
  )
}

/**
 * One card's failed read, with its own retry (§11.4: "Per panel. A failed superadmin read does
 * not blank the identity panel, and each panel carries its own retry").
 */
export function CardError({
  message,
  onRetry,
}: {
  readonly message: string
  readonly onRetry: () => void
}) {
  return (
    <div className="card-body">
      <Notice tone="failure">{message}</Notice>
      <div>
        <button type="button" className="button button-sm" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  )
}
